import React from "react";

/**
 * Formats a timestamp for display, tolerating nulls.
 * @param {string|null} value an ISO timestamp.
 * @returns {string}
 */
const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

/** One history row. Memoized so paging stays cheap. */
const HistoryRow = React.memo(function HistoryRow({ row }) {
  const recipients = row.recipients || [];
  const shown = recipients.slice(0, 2).join(", ");
  const extra = recipients.length > 2 ? ` +${recipients.length - 2}` : "";

  return (
    <tr>
      <td className="ehs-td">{row.ruleName || `Rule #${row.reminderRuleId}`}</td>
      <td className="ehs-td">{row.templateKey || "—"}</td>
      <td className="ehs-td" title={recipients.join(", ")}>
        {recipients.length ? `${shown}${extra}` : "—"}
      </td>
      <td className="ehs-td">{formatWhen(row.fireAt)}</td>
      <td className="ehs-td">{formatWhen(row.sentAt)}</td>
      <td className="ehs-td">
        <span className={`ehs-badge ehs-badge-${row.status}`}>
          {row.status}
        </span>
        {row.retryCount > 0 && (
          <span className="ehs-muted"> · {row.retryCount} retries</span>
        )}
      </td>
      <td className="ehs-td ehs-error-cell" title={row.lastError || ""}>
        {row.lastError || "—"}
      </td>
    </tr>
  );
});

/**
 * Read-only table of email send history.
 * @param {object} props
 * @param {Array<object>} props.rows the history rows.
 * @returns {JSX.Element}
 */
export default function EmailHistoryTable({ rows }) {
  if (!rows.length) {
    return (
      <p className="ehs-muted">
        Nothing sent yet. Once a reminder rule fires, every send shows up here.
      </p>
    );
  }

  return (
    <div className="ehs-table-wrap">
      <table className="ehs-table">
        <thead>
          <tr>
            <th className="ehs-th">Rule</th>
            <th className="ehs-th">Template</th>
            <th className="ehs-th">Recipients</th>
            <th className="ehs-th">Scheduled</th>
            <th className="ehs-th">Sent</th>
            <th className="ehs-th">Status</th>
            <th className="ehs-th">Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <HistoryRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
